import axios, {AxiosError, AxiosRequestHeaders, InternalAxiosRequestConfig} from 'axios';
// enums
import {STORAGE_KEY} from '@/utils/enums';
// services
import {adminStorage, orgStorage, userStorage} from '@/utils/services/storage.service';
// helpers
import {refreshTokens} from './helpers';

const baseURL = process.env.REACT_APP_API_URL;
const baseAppURL = process.env.REACT_APP_FE_URL;

export const authOrgAPI = axios.create({baseURL});
export const authUserAPI = axios.create({baseURL});
export const authAdminAPI = axios.create({baseURL});

function createAuthenticatedAPI(
  storage: typeof orgStorage,
  authAPI: typeof authOrgAPI,
  fallbackUrl: string | (() => string),
  identityKeys: STORAGE_KEY[] = []
) {
  const api = axios.create({baseURL});
  let refreshPromise: Promise<string | undefined> | null = null;

  const handleAuthFailure = (refreshError?: unknown, originalError?: AxiosError) => {
    // 1. Видаляємо токени ТІЛЬКИ для поточного сховища
    storage.remove(STORAGE_KEY.JWT_BEARER_TOKEN);
    storage.remove(STORAGE_KEY.REFRESH_TOKEN);
    identityKeys.forEach((key) => storage.remove(key));

    const url = typeof fallbackUrl === 'function' ? fallbackUrl() : fallbackUrl;
    const targetUrl = `${baseAppURL}${url}`;

    // 2. ЗАХИСТ ВІД ПЕТЛІ: порівнюємо саме поточний path, а не весь href.
    const currentPath = window.location.pathname;

    if (currentPath === url) {
      return Promise.reject(refreshError ?? originalError);
    }

    // 3. Робимо редірект один раз
    window.location.replace(targetUrl);

    return Promise.reject(refreshError ?? originalError);
  };

  api.interceptors.request.use(async (req) => {
    const accessToken = storage.get<string>(STORAGE_KEY.JWT_BEARER_TOKEN);
    if (accessToken) {
      req.headers = {
        ...(req.headers as AxiosRequestHeaders),
        Authorization: `Bearer ${accessToken}`,
      } as AxiosRequestHeaders;
    }
    return req;
  });

  api.interceptors.response.use(
    (res) => res,
    async (error: AxiosError) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & {_retry?: boolean};

      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;
        try {
          if (!refreshPromise) {
            refreshPromise = refreshTokens(storage, authAPI).finally(() => {
              refreshPromise = null;
            });
          }
          const newToken = await refreshPromise;

          if (newToken) {
            originalRequest.headers = {
              ...(originalRequest.headers as AxiosRequestHeaders),
              Authorization: `Bearer ${newToken}`,
            } as AxiosRequestHeaders;
            return api(originalRequest);
          }

          return handleAuthFailure(undefined, error);
        } catch (refreshError) {
          return handleAuthFailure(refreshError, error);
        }
      }
      return Promise.reject(error);
    }
  );

  return api;
}

export const orgAPI = createAuthenticatedAPI(orgStorage, authOrgAPI, '/login', [
  STORAGE_KEY.ORGANIZATION_ID,
  STORAGE_KEY.TEAM_MEMBER_ID,
  STORAGE_KEY.ORGANIZATION_ROLE,
]);
export const userAPI = createAuthenticatedAPI(
  userStorage,
  authUserAPI,
  () => {
    const eventSlug = userStorage.get<string>(STORAGE_KEY.ORGANIZATION_EVENT_SLUG);
    return eventSlug ? `/${eventSlug}` : '/';
  },
  [STORAGE_KEY.USER_ID]
);

export const adminAPI = createAuthenticatedAPI(adminStorage, authAdminAPI, '/admin');
